import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { parseImageFilename, applyScannedImages } from '../utils/lotImages';
import toast from 'react-hot-toast';

const UpdateImages: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState<any>(null);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);

  const updateAuctionImages = async () => {
    if (!auctionId) return;
    
    setProcessing(true);
    setProgress('Loading auction data...');
    setDebugInfo([]);
    const debug: string[] = [];
    
    try {
      // Load auction from Firestore
      const auctionDoc = await getDoc(doc(db, 'auctions', auctionId));
      if (!auctionDoc.exists()) {
        toast.error('Auction not found');
        return;
      }

      const auction = auctionDoc.data() as Auction;
      const lots = Array.isArray(auction.lots) ? auction.lots : [];
      setProgress(`Found ${lots.length} lots`);

      // What is already there, measured BEFORE the scan. Every guard below
      // compares against this: the tool may raise this number, never lower it.
      const withImagesBefore = lots.filter(l => (l.images || []).filter(Boolean).length > 0).length;
      debug.push(`Before scan: ${withImagesBefore} of ${lots.length} lots already have images`);

      // Debug: Show first 5 lot SKUs
      const sampleLotSkus = lots.slice(0, 5).map(l => l.sku);
      debug.push(`Sample Lot SKUs: ${sampleLotSkus.join(', ')}`);

      // Build image map: SKU -> [URLs]
      const imageMap: Record<string, string[]> = {};
      let totalImages = 0;
      // Files in the folder that are NOT originals. Counted and reported rather
      // than silently dropped — a folder full of skips means something upstream
      // changed and the operator needs to see it.
      const skipped: string[] = [];

      setProgress('Scanning Firebase Storage for images...');

      // Scan each image folder
      const basePath = `auctions/${auctionId}/images`;
      const baseRef = ref(storage, basePath);
      
      try {
        const folderList = await listAll(baseRef);
        debug.push(`Found ${folderList.prefixes.length} subfolders`);

        // REFUSE #1 — nothing to scan.
        // "Found 0 subfolders" is a reason to stop, not a reason to write empty
        // arrays over 104 lots. This tool has no business emptying anything.
        if (folderList.prefixes.length === 0) {
          const msg = `No image folders found under ${basePath} — refusing to write.`;
          debug.push(msg);
          debug.push('Nothing was changed. The lots keep whatever images they already had.');
          debug.push('If the images live somewhere other than Firebase Storage (e.g. Cloudinary), this tool is not the right one.');
          setResults({ refused: true, reason: `No image folders under ${basePath}`, totalLots: lots.length });
          setProgress('Refused — nothing scanned, nothing written.');
          setDebugInfo(debug);
          toast.error('No images found in Storage — nothing was changed.');
          return;
        }

        // For each subfolder (e.g., "01-06-26 Dan")
        for (const folderRef of folderList.prefixes) {
          setProgress(`Scanning folder: ${folderRef.name}...`);
          
          const imageList = await listAll(folderRef);
          debug.push(`Folder ${folderRef.name}: ${imageList.items.length} images`);
          
          // Debug: Show first 5 filenames from first folder
          if (folderList.prefixes.indexOf(folderRef) === 0) {
            const sampleFilenames = imageList.items.slice(0, 5).map(i => i.name);
            debug.push(`Sample filenames: ${sampleFilenames.join(', ')}`);
            
            const sampleSkus = sampleFilenames.map(f => parseImageFilename(f)?.sku ?? '(rejected)');
            debug.push(`Extracted SKUs: ${sampleSkus.join(', ')}`);
          }
          
          // For each image file
          for (const imageRef of imageList.items) {
            const filename = imageRef.name;

            // ONLY {sku}_NN.ext counts as an original. The old rule was
            // filename.split('_')[0], which happily accepted anything with an
            // underscore in it — a thumbnail (700001_00_200x200.jpg), a
            // re-download (700001_00 (1).jpg), a stray .DS_Store. Every one of
            // those would have been appended to a lot's images[] as if it were
            // a photo the auctioneer had taken.
            const parsed = parseImageFilename(filename);
            if (!parsed) {
              skipped.push(filename);
              continue;
            }
            const sku = parsed.sku;

            // Get download URL
            const url = await getDownloadURL(imageRef);

            if (!imageMap[sku]) {
              imageMap[sku] = [];
            }
            imageMap[sku].push(url);
            totalImages++;
          }
        }
        
        debug.push(`ImageMap keys: ${Object.keys(imageMap).slice(0, 10).join(', ')}`);
        if (skipped.length) {
          debug.push(`SKIPPED ${skipped.length} non-original file(s): ${skipped.slice(0, 10).join(', ')}${skipped.length > 10 ? ' …' : ''}`);
        }
        setProgress(`Found ${totalImages} images for ${Object.keys(imageMap).length} lots`);

        // REFUSE #2 — folders existed but held no usable originals.
        if (totalImages === 0) {
          const msg = `Scanned ${folderList.prefixes.length} folder(s) and found 0 usable images — refusing to write.`;
          debug.push(msg);
          if (skipped.length) debug.push(`All ${skipped.length} file(s) were rejected by the {sku}_NN.ext rule — see the skipped list above.`);
          debug.push('Nothing was changed.');
          setResults({ refused: true, reason: msg, totalLots: lots.length, skipped: skipped.length });
          setProgress('Refused — no usable images, nothing written.');
          setDebugInfo(debug);
          toast.error('Found 0 usable images — nothing was changed.');
          return;
        }

        // Merge additively. applyScannedImages leaves a lot alone when the scan
        // has nothing for it, so images from another source (Cloudinary, a
        // manual fix) survive a partial scan instead of being wiped.
        // Sorting is by FILENAME, not URL string: a Firebase download URL ends
        // `…700001_01.jpg?alt=media&token=<random uuid>`, so a plain .sort()
        // orders by the random token and can float a _00 barcode to first.
        const merged = applyScannedImages(lots, imageMap);
        const updatedLots = merged.lots;
        debug.push(`Merge: ${merged.updated} lot(s) updated from the scan, ${merged.preserved} kept images the scan had nothing for`);

        // REFUSE #3 — the invariant, checked rather than assumed.
        // applyScannedImages cannot reduce the count; if it somehow did, stop
        // before writing rather than discovering it afterwards on the live site.
        if (merged.withImagesAfter < withImagesBefore) {
          const msg = `Refusing to write: this would drop ${withImagesBefore - merged.withImagesAfter} lot(s) from having images (${withImagesBefore} → ${merged.withImagesAfter}).`;
          debug.push(msg);
          setResults({ refused: true, reason: msg, totalLots: lots.length });
          setProgress('Refused — the write would have removed images.');
          setDebugInfo(debug);
          toast.error('Refused: that write would have removed images.');
          return;
        }

        if (merged.updated === 0) {
          debug.push('No lot matched a scanned SKU — nothing to write.');
          setResults({ refused: true, reason: 'No lot SKU matched any scanned filename.', totalLots: lots.length, totalImages });
          setProgress('Refused — no SKU matches, nothing written.');
          setDebugInfo(debug);
          toast.error('No lot SKUs matched the files in Storage — nothing was changed.');
          return;
        }

        // Update Firestore
        setProgress('Updating Firestore...');
        await updateDoc(doc(db, 'auctions', auctionId), {
          lots: updatedLots,
        });

        setResults({
          totalLots: lots.length,
          lotsWithImages: merged.withImagesAfter,
          lotsWithoutImages: lots.length - merged.withImagesAfter,
          withImagesBefore,
          preserved: merged.preserved,
          totalImages,
          skipped: skipped.length,
        });

        toast.success('Images updated successfully!');
        setProgress('Complete!');
        setDebugInfo(debug);

      } catch (storageError: any) {
        console.error('Storage error:', storageError);
        toast.error(`Storage error: ${storageError.message}`);
        setProgress(`Error: ${storageError.message}`);
        debug.push(`Storage Error: ${storageError.message}`);
        setDebugInfo(debug);
      }

    } catch (error: any) {
      console.error('Error updating images:', error);
      toast.error('Failed to update images');
      setProgress(`Error: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Update Auction Images</h1>
        <p className="text-gray-600 mt-1">
          Auction ID: {auctionId}
        </p>
      </div>

      <div className="card">
        <h2 className="text-xl font-bold mb-4">Image URL Generator</h2>
        
        <p className="text-gray-600 mb-6">
          This tool will scan Firebase Storage for images, match them to lots by SKU, 
          and update the auction with the correct image URLs.
        </p>

        <button
          onClick={updateAuctionImages}
          disabled={processing}
          className="btn-primary w-full"
        >
          {processing ? 'Processing...' : 'Update All Images'}
        </button>

        {progress && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-medium">{progress}</p>
          </div>
        )}

        {debugInfo.length > 0 && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-bold text-yellow-800 mb-2">Debug Info:</h3>
            <ul className="space-y-1 text-yellow-700 text-sm font-mono">
              {debugInfo.map((info, i) => (
                <li key={i}>{info}</li>
              ))}
            </ul>
          </div>
        )}

        {results?.refused && (
          <div className="mt-6 p-4 bg-red-50 border border-red-300 rounded-lg">
            <h3 className="font-bold text-red-800 mb-2">Refused — nothing was written</h3>
            <p className="text-red-700">{results.reason}</p>
            <p className="text-sm text-red-600 mt-2">
              Your lots are untouched. This tool only adds images from Firebase Storage; it will not
              empty a lot that already has images, and it will not write when a scan finds nothing.
            </p>
          </div>
        )}

        {results && !results.refused && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="font-bold text-green-800 mb-2">Results:</h3>
            <ul className="space-y-1 text-green-700">
              <li>✅ Total Lots: {results.totalLots}</li>
              <li>✅ Lots with Images: {results.lotsWithImages} (was {results.withImagesBefore})</li>
              <li>⚠️ Lots without Images: {results.lotsWithoutImages}</li>
              <li>📸 Total Images: {results.totalImages}</li>
              {results.preserved > 0 && (
                <li>🛡️ Left alone (already had images, scan had none): {results.preserved}</li>
              )}
              {results.skipped > 0 && (
                <li>🚫 Skipped (not named {'{sku}_NN.ext'}): {results.skipped}</li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};


export default UpdateImages;
